import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { AgentConnectionsCard } from './AgentConnectionsCard';
import { ComplianceConfigForm } from './ComplianceConfigForm';
import { OrchestratorConfigForm } from './OrchestratorConfigForm';
import { PaymentConfigForm } from './PaymentConfigForm';
import { PerpetualsConfigForm } from './PerpetualsConfigForm';
import { TradingConfigForm } from './TradingConfigForm';
import { TreasuryConfigForm } from './TreasuryConfigForm';
import type { Agent } from './api';

type Props = {
  orgId: string;
  agent: Agent;
};

export function AgentConfigurationTab({ orgId, agent }: Props) {
  return (
    <div className="space-y-6">
      <TemplateConfig orgId={orgId} agent={agent} />
      <AgentConnectionsCard orgId={orgId} agentId={agent.id} />
      <ToolingCard agent={agent} />
    </div>
  );
}

function TemplateConfig({ orgId, agent }: Props) {
  if (agent.template_slug === 'payment') {
    return <PaymentConfigForm orgId={orgId} agent={agent} />;
  }
  if (agent.template_slug === 'trading') {
    return <TradingConfigForm orgId={orgId} agent={agent} />;
  }
  if (agent.template_slug === 'orchestrator') {
    return <OrchestratorConfigForm orgId={orgId} agent={agent} />;
  }
  if (agent.template_slug === 'treasury') {
    return <TreasuryConfigForm orgId={orgId} agent={agent} />;
  }
  if (agent.template_slug === 'perpetuals') {
    return <PerpetualsConfigForm orgId={orgId} agent={agent} />;
  }
  if (agent.template_slug === 'compliance') {
    return <ComplianceConfigForm orgId={orgId} agent={agent} />;
  }
  return (
    <Card>
      <CardHeader
        title="Configuration"
        description="Generic agents take their behaviour entirely from the system prompt. No structured config required."
      />
    </Card>
  );
}

function ToolingCard({ agent }: { agent: Agent }) {
  return (
    <Card>
      <CardHeader
        title="Tooling"
        description="What this agent is allowed to call. Quotas and per-run limits are also shown here."
      />
      <CardBody className="space-y-3 text-sm">
        <Row label="Enabled tools" mono>
          {agent.enabled_tools.length === 0 ? '—' : agent.enabled_tools.join(', ')}
        </Row>
        {agent.mcp_server_ids.length > 0 && (
          <Row label="MCP servers" mono>
            {agent.mcp_server_ids.join(', ')}
          </Row>
        )}
        <Row label="Max runs/day">
          {agent.max_runs_per_day ?? <span className="text-ink-tertiary">default</span>}
        </Row>
        <Row label="Max tokens/run">
          {agent.max_tokens_per_run ?? <span className="text-ink-tertiary">default</span>}
        </Row>
        <Row label="Max seconds/run">
          {agent.max_seconds_per_run ?? <span className="text-ink-tertiary">default</span>}
        </Row>
      </CardBody>
    </Card>
  );
}

function Row({
  label,
  mono = false,
  children,
}: {
  label: string;
  mono?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-3 gap-3">
      <div className="text-xs uppercase tracking-eyebrow text-ink-tertiary">{label}</div>
      <div className={`col-span-2 ${mono ? 'font-mono break-all text-xs' : 'text-sm'} text-ink-primary`}>
        {children}
      </div>
    </div>
  );
}
